import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaSyncAlt,
  FaExclamationTriangle,
  FaTicketAlt,
  FaSave,
  FaTimes,
  FaEdit,
  FaTruck,
  FaCheck,
  FaPlus,
  FaChevronDown,
  FaChevronRight,
  FaExternalLinkAlt,
  FaBan,
  FaShareSquare,
  FaKey,
  FaCopy,
  FaTrashAlt,
  FaPlug,
  FaRobot,
} from 'react-icons/fa';
import './ClientEDNDetail.css';
import Button from './UI/Button';
import { ALL_STATUS_MAPPING, getStatusClass, getStatusLabel } from '../constants/ticketStatuses';

const TICKETS_PAGE_SIZE = 50;

// Stored in English: the backend AI prompt consumes the value directly.
export const SOUFFRANCE_LANGUAGES = [
  'French',
  'English',
  'German',
  'Spanish',
  'Italian',
  'Dutch',
  'Portuguese',
  'Polish',
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('fr-FR');
};

const ClientEDNDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [client, setClient] = useState(null);
  const [clientLoading, setClientLoading] = useState(true);
  const [clientError, setClientError] = useState(null);

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState(null);
  const [ticketsHasMore, setTicketsHasMore] = useState(false);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [carriers, setCarriers] = useState([]);
  const [showAdvancedRouting, setShowAdvancedRouting] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showApiSection, setShowApiSection] = useState(false);

  const [apiAccess, setApiAccess] = useState(null);
  const [apiAccessLoading, setApiAccessLoading] = useState(true);
  const [apiAccessBusy, setApiAccessBusy] = useState(false);
  const [apiAccessError, setApiAccessError] = useState(null);
  const [generatedApiKey, setGeneratedApiKey] = useState(null);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);

  // Tickets list state (page + filters) lives in the URL so browser back
  // restores it after opening a ticket (frontend-design.md).
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get('tpage') || '1', 10);
  const ticketsPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const statusFilter = searchParams.get('tstatus') || '';
  const carrierFilter = searchParams.get('tcarrier') || '';

  const updateTicketParams = (updates, { replace = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace });
  };

  const authHeaders = useMemo(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (!token) return null;
    return { Authorization: `Bearer ${token}` };
  }, []);

  const apiBaseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  useEffect(() => {
    if (!authHeaders) navigate('/');
  }, [authHeaders, navigate]);

  const fetchClient = useCallback(async () => {
    if (!authHeaders) return;
    setClientLoading(true);
    setClientError(null);
    try {
      const response = await axios.get('/api/logistics/clients-edn', { headers: authHeaders });
      const found = response.data.find((c) => c.id === clientId);
      if (found) {
        setClient(found);
      } else {
        setClientError(t('clientEDNDetail.notFound', 'Client not found'));
      }
    } catch (err) {
      setClientError(err.response?.data?.detail || err.message || 'Failed to load client');
    } finally {
      setClientLoading(false);
    }
  }, [authHeaders, clientId, t]);

  const fetchTickets = useCallback(async () => {
    if (!authHeaders) return;
    setTicketsLoading(true);
    setTicketsError(null);
    try {
      const params = {
        skip: (ticketsPage - 1) * TICKETS_PAGE_SIZE,
        limit: TICKETS_PAGE_SIZE,
      };
      if (statusFilter) params.status = statusFilter;
      if (carrierFilter) params.carrier = carrierFilter;

      const response = await axios.get(`/api/logistics/clients-edn/${clientId}/tickets`, {
        headers: authHeaders,
        params,
      });
      const payload = Array.isArray(response.data) ? response.data : [];
      setTickets(payload);
      setTicketsHasMore(payload.length === TICKETS_PAGE_SIZE);
    } catch (err) {
      setTicketsError(err.response?.data?.detail || err.message || 'Failed to load tickets');
      setTickets([]);
      setTicketsHasMore(false);
    } finally {
      setTicketsLoading(false);
    }
  }, [authHeaders, clientId, statusFilter, carrierFilter, ticketsPage]);

  const fetchStats = useCallback(async () => {
    if (!authHeaders) return;
    setStatsLoading(true);
    try {
      const response = await axios.get(`/api/logistics/clients-edn/${clientId}/stats`, {
        headers: authHeaders,
      });
      setStats(response.data);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [authHeaders, clientId]);

  const fetchCarriers = useCallback(async () => {
    if (!authHeaders) return;
    try {
      const response = await axios.get('/api/logistics/carriers', { headers: authHeaders });
      setCarriers(response.data);
    } catch {
      setCarriers([]);
    }
  }, [authHeaders]);

  const fetchApiAccess = useCallback(async () => {
    if (!authHeaders) return;
    setApiAccessLoading(true);
    setApiAccessError(null);
    try {
      const response = await axios.get(
        `/api/logistics/clients-edn/${clientId}/api-access`,
        { headers: authHeaders },
      );
      setApiAccess(response.data);
    } catch (err) {
      setApiAccess(null);
      setApiAccessError(err.response?.data?.detail || err.message || 'Failed to load API access');
    } finally {
      setApiAccessLoading(false);
    }
  }, [authHeaders, clientId]);

  const handleGenerateApiKey = useCallback(async () => {
    if (!authHeaders) return;
    setApiAccessBusy(true);
    setApiAccessError(null);
    setApiKeyCopied(false);
    try {
      const response = await axios.post(
        `/api/logistics/clients-edn/${clientId}/api-access`,
        {},
        { headers: authHeaders },
      );
      setGeneratedApiKey(response.data.api_key);
      setApiAccess({
        client_edn_id: response.data.client_edn_id,
        enabled: response.data.enabled,
        key_prefix: response.data.key_prefix,
        created_at: response.data.created_at,
        last_used_at: response.data.last_used_at,
        revoked_at: response.data.revoked_at,
      });
    } catch (err) {
      setApiAccessError(err.response?.data?.detail || err.message || 'Failed to generate API key');
    } finally {
      setApiAccessBusy(false);
    }
  }, [authHeaders, clientId]);

  const handleRevokeApiKey = useCallback(async () => {
    if (!authHeaders) return;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      t('clientEDNDetail.apiRevokeConfirm', 'Revoke API access for this client? Their ERP integration will stop working immediately.'),
    );
    if (!confirmed) return;
    setApiAccessBusy(true);
    setApiAccessError(null);
    try {
      const response = await axios.delete(
        `/api/logistics/clients-edn/${clientId}/api-access`,
        { headers: authHeaders },
      );
      setApiAccess(response.data);
      setGeneratedApiKey(null);
    } catch (err) {
      setApiAccessError(err.response?.data?.detail || err.message || 'Failed to revoke API key');
    } finally {
      setApiAccessBusy(false);
    }
  }, [authHeaders, clientId, t]);

  const handleCopyApiKey = useCallback(async () => {
    if (!generatedApiKey) return;
    try {
      await navigator.clipboard.writeText(generatedApiKey);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
    } catch {
      setApiKeyCopied(false);
    }
  }, [generatedApiKey]);

  useEffect(() => {
    fetchClient();
    fetchStats();
    fetchCarriers();
    fetchApiAccess();
  }, [fetchClient, fetchStats, fetchCarriers, fetchApiAccess]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleBack = () => navigate('/clients-instructions');

  const handleTicketClick = (ticketId) => navigate(`/tickets/${ticketId}/messages`);

  const handleStartEdit = () => {
    const routing = client.email_routing || {};
    setEditForm({
      name: client.name || '',
      client_id: client.client_id || '',
      emails: client.emails || [],
      instructions: client.instructions || '',
      reference: client.reference || '',
      automation: client.automation || false,
      disabled_carriers: client.disabled_carriers || [],
      aliases: client.aliases || [],
      default_email: routing.default_email || '',
      b2b_email: routing.b2b_email || '',
      b2c_email: routing.b2c_email || '',
      effitrace_url: client.effitrace_url || '',
      souffrance_language: client.souffrance_language || '',
      souffrance_instructions: client.souffrance_instructions || '',
    });
    setShowAdvancedRouting(!!(routing.b2b_email || routing.b2c_email));
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!authHeaders) return;
    setSaving(true);
    setSaveError(null);
    try {
      const emailRouting = {};
      if (editForm.default_email) emailRouting.default_email = editForm.default_email;
      if (editForm.b2b_email) emailRouting.b2b_email = editForm.b2b_email;
      if (editForm.b2c_email) emailRouting.b2c_email = editForm.b2c_email;
      const emailRoutingPayload = Object.keys(emailRouting).length > 0 ? emailRouting : null;

      const response = await axios.put(
        `/api/logistics/clients-edn/${clientId}`,
        {
          name: editForm.name,
          client_id: editForm.client_id || null,
          emails: editForm.emails,
          instructions: editForm.instructions,
          reference: editForm.reference,
          automation: editForm.automation,
          disabled_carriers: editForm.disabled_carriers,
          aliases: editForm.aliases,
          email_routing: emailRoutingPayload,
          effitrace_url: editForm.effitrace_url || null,
          // Empty strings (not null) so clearing a field actually persists
          souffrance_language: editForm.souffrance_language ?? '',
          souffrance_instructions: editForm.souffrance_instructions ?? '',
        },
        { headers: authHeaders },
      );
      setClient(response.data);
      setIsEditing(false);
    } catch (err) {
      setSaveError(err.response?.data?.detail || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    fetchTickets();
    fetchStats();
  };

  // Relative wording for recent tickets (full date in the tooltip)
  const formatRelativeDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    const diffMinutes = Math.round((Date.now() - date.getTime()) / 60000);
    if (diffMinutes < 1) return t('justNow', "À l'instant");
    if (diffMinutes < 60) return t('minutesAgo', 'il y a {{count}} min', { count: diffMinutes });
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return t('hoursAgo', 'il y a {{count}} h', { count: diffHours });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Activity data — composition of the client's tickets by status, plus
  // proportion meters for carriers and topics (all from the stats endpoint).
  const activity = useMemo(() => {
    if (!stats) return null;
    const total = Number(stats.total_tickets) || 0;
    const byStatus = (stats.tickets_by_status || [])
      .filter((s) => s.count > 0)
      .sort((a, b) => b.count - a.count);
    const shown = byStatus.slice(0, 6);
    const restCount = byStatus.slice(6).reduce((sum, s) => sum + s.count, 0);
    const carriersTop = (stats.top_carriers || []).slice(0, 5);
    const carriersMax = Math.max(1, ...carriersTop.map((c) => c.count));
    const topicsTop = (stats.topics_distribution || []).slice(0, 5);
    const topicsMax = Math.max(1, ...topicsTop.map((tp) => tp.count));
    return { total, byStatus, shown, restCount, carriersTop, carriersMax, topicsTop, topicsMax };
  }, [stats]);

  if (!authHeaders) return null;

  const definitionRow = (label, value) => (
    <div className="edn-def-row">
      <span className="edn-def-label">{label}</span>
      <span className="edn-def-value">{value}</span>
    </div>
  );

  // An alias the AI added after verifying the enseigne on a parcel (read-only).
  const aliasProvenance = (alias) => (client?.alias_provenance || []).find(
    (entry) => String(entry.alias || '').trim().toLowerCase() === String(alias || '').trim().toLowerCase(),
  );

  const aliasProvenanceText = (entry) => t(
    'clientEDNDetail.aliasProvenance',
    "« {{alias}} » ajouté par l'IA le {{date}} : enseigne vérifiée sur {{carrier}} (colis {{tracking}} retrouvé sous cette enseigne).",
    {
      alias: entry.alias,
      date: formatDateTime(entry.at),
      carrier: entry.carrier || t('clientEDNDetail.aliasProvenanceCarrier', 'le portail transporteur'),
      tracking: entry.verified_tracking_number || '—',
    },
  );

  return (
    <div className="edn-detail-container">
      {/* Page header: back + identity + actions */}
      <header className="edn-detail-header">
        <div className="edn-header-left">
          <button type="button" className="edn-back-button" onClick={handleBack} aria-label={t('clientEDNDetail.backToB2B', 'Retour aux clients B2B')}>
            <FaArrowLeft />
          </button>
          {client && !clientLoading && (
            <div className="edn-header-identity">
              <h1>{client.name}</h1>
              {client.client_id && <span className="edn-id-chip">{client.client_id}</span>}
              <span className={`edn-auto-state ${client.automation ? 'on' : 'off'}`}>
                <span className="edn-state-dot" aria-hidden="true" />
                {client.automation
                  ? t('clientEDNDetail.automationOn', 'Automatisation active')
                  : t('clientEDNDetail.automationOff', 'Automatisation désactivée')}
              </span>
            </div>
          )}
        </div>
        <div className="edn-header-actions">
          {!isEditing && client && (
            <button type="button" className="u-btn u-btn--primary" onClick={handleStartEdit}>
              <FaEdit />
              {t('clientsInstructions.editClient', 'Modifier')}
            </button>
          )}
          <button
            type="button"
            className="u-btn u-btn--secondary"
            onClick={handleRefresh}
            disabled={ticketsLoading || statsLoading}
          >
            <FaSyncAlt />
            {t('clientEDNDetail.refresh', 'Actualiser')}
          </button>
        </div>
      </header>

      {clientLoading ? (
        <div className="edn-card">
          <div className="edn-skeleton" aria-busy="true">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="edn-skeleton-row" />
            ))}
          </div>
        </div>
      ) : clientError ? (
        <div className="edn-card">
          <div className="edn-detail-state edn-detail-state-error">
            <FaExclamationTriangle />
            <p>{clientError}</p>
          </div>
        </div>
      ) : client && (
        <div className="edn-detail-grid">
          {/* CONFIGURATION card (view or sectioned edit form) */}
          <section className="edn-card edn-config-card">
            <div className="edn-card-title">
              <h2>{t('clientEDNDetail.configuration', 'Configuration')}</h2>
              <span className="edn-card-title-meta">
                {t('common.updatedAt', 'Maj le')} {formatDateTime(client.updated_at)}
              </span>
            </div>

            {!isEditing ? (
              <div className="edn-config-view">
                {definitionRow(
                  t('clientsInstructions.emails', 'Emails'),
                  client.emails?.length > 0 ? (
                    <span className="edn-chip-list">
                      {client.emails.map((email) => (
                        <span key={email} className="edn-chip">{email}</span>
                      ))}
                    </span>
                  ) : (
                    <span className="edn-flag-warn">
                      <span className="edn-warn-dot" aria-hidden="true" />
                      {t('clientEDNDetail.noneConfigured', 'aucun')}
                    </span>
                  ),
                )}

                {definitionRow(
                  t('clientEDNDetail.aliases', 'Alias'),
                  client.aliases?.length > 0 ? (
                    <span className="edn-alias-block">
                      <span className="edn-chip-list">
                        {client.aliases.map((alias) => (
                          aliasProvenance(alias) ? (
                            <span
                              key={alias}
                              className="edn-chip edn-chip--ai"
                              title={aliasProvenanceText(aliasProvenance(alias))}
                            >
                              <FaRobot aria-label={t('clientEDNDetail.aliasByAi', "Ajouté par l'IA")} />
                              {alias}
                            </span>
                          ) : (
                            <span key={alias} className="edn-chip">{alias}</span>
                          )
                        ))}
                      </span>
                      {(client.alias_provenance || []).map((entry) => (
                        <span key={`prov-${entry.alias}`} className="edn-alias-provenance">
                          {aliasProvenanceText(entry)}
                          {entry.ticket_id && (
                            <button
                              type="button"
                              className="edn-alias-provenance-link"
                              onClick={() => handleTicketClick(entry.ticket_id)}
                            >
                              {t('clientEDNDetail.aliasProvenanceTicket', 'Voir le ticket')}
                            </button>
                          )}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="edn-muted-dash">–</span>
                  ),
                )}

                {definitionRow(
                  t('clientsInstructions.reference', 'Référence'),
                  client.reference ? (
                    <span className="edn-def-text">{client.reference}</span>
                  ) : (
                    <span className="edn-muted-dash">–</span>
                  ),
                )}

                {definitionRow(
                  t('clientEDNDetail.effitraceUrl', 'URL Effitrace'),
                  client.effitrace_url ? (
                    <a
                      href={client.effitrace_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="edn-link"
                    >
                      {client.effitrace_url} <FaExternalLinkAlt aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="edn-muted-dash">–</span>
                  ),
                )}

                {definitionRow(
                  t('clientsList.disabledCarriers', 'Transp. désactivés'),
                  client.disabled_carriers?.length > 0 ? (
                    <span className="edn-chip-list">
                      {client.disabled_carriers.map((carrier) => (
                        <span key={carrier} className="edn-chip edn-chip--warn">
                          <FaBan aria-hidden="true" /> {carrier}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="edn-muted-dash">–</span>
                  ),
                )}

                {client.email_routing && Object.keys(client.email_routing).length > 0 && definitionRow(
                  t('clientEDNDetail.emailRouting', 'Notifications'),
                  <span className="edn-routing-values">
                    {client.email_routing.default_email && (
                      <span>{t('clientEDNDetail.defaultShort', 'Défaut')} · {client.email_routing.default_email}</span>
                    )}
                    {client.email_routing.b2b_email && <span>B2B · {client.email_routing.b2b_email}</span>}
                    {client.email_routing.b2c_email && <span>B2C · {client.email_routing.b2c_email}</span>}
                  </span>,
                )}

                {definitionRow(
                  t('clientEDNDetail.souffranceLanguage', 'Langue souffrance'),
                  <span className="edn-def-text">
                    {client.souffrance_language
                      ? t(`clientEDNDetail.languages.${client.souffrance_language}`, client.souffrance_language)
                      : t('clientEDNDetail.souffranceLanguageDefault', 'Par défaut (français)')}
                  </span>,
                )}

                {/* Long texts behind disclosures */}
                <div className="edn-disclosure">
                  <button
                    type="button"
                    className="edn-disclosure-toggle"
                    onClick={() => setShowInstructions((v) => !v)}
                    aria-expanded={showInstructions}
                  >
                    {showInstructions ? <FaChevronDown /> : <FaChevronRight />}
                    {t('clientsInstructions.instructions', 'Instructions')}
                    {client.instructions ? (
                      <span className="edn-flag-ok"><FaCheck aria-hidden="true" /></span>
                    ) : (
                      <span className="edn-flag-warn">
                        <span className="edn-warn-dot" aria-hidden="true" />
                        {t('clientsList.missing', 'manquantes')}
                      </span>
                    )}
                  </button>
                  {showInstructions && (
                    <div className="edn-disclosure-body">
                      {client.instructions
                        ? <p>{client.instructions}</p>
                        : <p className="edn-muted-text">{t('clientEDNDetail.noInstructions', 'Aucune instruction pour ce client.')}</p>}
                      {client.souffrance_instructions && (
                        <>
                          <h4>{t('clientEDNDetail.souffranceInstructions', 'Instructions souffrance')}</h4>
                          <p>{client.souffrance_instructions}</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="edn-edit-form">
                {saveError && (
                  <div className="edn-alert edn-alert-error">{saveError}</div>
                )}

                <h3 className="edn-form-section-title">{t('clientEDNDetail.sectionIdentity', 'Identité')}</h3>
                <div className="edn-form-grid">
                  <div className="edn-form-group">
                    <label>{t('clientsInstructions.name', 'Nom')}</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                      className="edn-input"
                    />
                  </div>
                  <div className="edn-form-group">
                    <label>{t('clientsInstructions.clientId', 'ID client')}</label>
                    <input
                      type="text"
                      value={editForm.client_id}
                      onChange={(e) => setEditForm((f) => ({ ...f, client_id: e.target.value }))}
                      className="edn-input"
                    />
                  </div>
                </div>
                <div className="edn-form-group">
                  <label>{t('clientEDNDetail.aliases', 'Alias')}</label>
                  {(editForm.aliases || []).map((alias, index) => (
                    <React.Fragment key={index}>
                    <div className="edn-email-row">
                      <input
                        type="text"
                        value={alias}
                        onChange={(e) => setEditForm((f) => {
                          const updated = [...(f.aliases || [])];
                          updated[index] = e.target.value;
                          return { ...f, aliases: updated };
                        })}
                        placeholder={t('clientEDNDetail.aliasPlaceholder', "Nom d'alias")}
                        className="edn-input"
                      />
                      <button
                        type="button"
                        className="edn-remove-email-btn"
                        onClick={() => setEditForm((f) => {
                          const updated = [...(f.aliases || [])];
                          updated.splice(index, 1);
                          return { ...f, aliases: updated };
                        })}
                        aria-label={t('common.delete', 'Supprimer')}
                      >
                        <FaTimes />
                      </button>
                    </div>
                    {aliasProvenance(alias) && (
                      <p className="edn-field-hint edn-alias-provenance-hint">
                        <FaRobot aria-hidden="true" /> {aliasProvenanceText(aliasProvenance(alias))}
                      </p>
                    )}
                    </React.Fragment>
                  ))}
                  <button
                    type="button"
                    className="edn-add-email-btn"
                    onClick={() => setEditForm((f) => ({ ...f, aliases: [...(f.aliases || []), ''] }))}
                  >
                    <FaPlus /> {t('clientEDNDetail.addAlias', 'Ajouter un alias')}
                  </button>
                </div>

                <h3 className="edn-form-section-title">{t('clientEDNDetail.sectionDetection', 'Détection des e-mails')}</h3>
                <div className="edn-form-group">
                  <label>{t('clientsInstructions.emails', 'Emails')}</label>
                  <p className="edn-field-hint">{t('clientsInstructions.emailsHint', "Sert à identifier les e-mails entrants de ce client — adresses complètes ou domaines (ex. brand.com).")}</p>
                  {editForm.emails.map((email, index) => (
                    <div key={index} className="edn-email-row">
                      <input
                        type="text"
                        value={email}
                        onChange={(e) => setEditForm((f) => {
                          const updated = [...f.emails];
                          updated[index] = e.target.value;
                          return { ...f, emails: updated };
                        })}
                        placeholder={t('clientsInstructions.emailPlaceholder', 'Adresse ou domaine (ex. brand.com)')}
                        className="edn-input"
                      />
                      <button
                        type="button"
                        className="edn-remove-email-btn"
                        onClick={() => setEditForm((f) => {
                          const updated = [...f.emails];
                          updated.splice(index, 1);
                          return { ...f, emails: updated };
                        })}
                        aria-label={t('common.delete', 'Supprimer')}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="edn-add-email-btn"
                    onClick={() => setEditForm((f) => ({ ...f, emails: [...f.emails, ''] }))}
                  >
                    <FaPlus /> {t('clientsInstructions.addEmail', 'Ajouter un email')}
                  </button>
                </div>

                <h3 className="edn-form-section-title">{t('clientEDNDetail.sectionInstructions', 'Instructions IA')}</h3>
                <div className="edn-form-group">
                  <label>{t('clientsInstructions.instructions', 'Instructions')}</label>
                  <textarea
                    value={editForm.instructions}
                    onChange={(e) => setEditForm((f) => ({ ...f, instructions: e.target.value }))}
                    rows={6}
                    className="edn-textarea"
                  />
                </div>
                <div className="edn-form-grid">
                  <div className="edn-form-group">
                    <label>{t('clientsInstructions.reference', 'Référence')}</label>
                    <input
                      type="text"
                      value={editForm.reference}
                      onChange={(e) => setEditForm((f) => ({ ...f, reference: e.target.value }))}
                      className="edn-input"
                    />
                  </div>
                  <div className="edn-form-group">
                    <label>{t('clientEDNDetail.effitraceUrl', 'URL Effitrace')}</label>
                    <input
                      type="url"
                      value={editForm.effitrace_url || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, effitrace_url: e.target.value }))}
                      placeholder="https://..."
                      className="edn-input"
                    />
                  </div>
                </div>

                <h3 className="edn-form-section-title">{t('clientEDNDetail.sectionAutomation', 'Automatisation & transporteurs')}</h3>
                <div className="edn-form-group">
                  <label className="edn-inline-toggle">
                    <input
                      type="checkbox"
                      checked={editForm.automation || false}
                      onChange={(e) => setEditForm((f) => ({ ...f, automation: e.target.checked }))}
                    />
                    {t('clientEDNDetail.automationEnabled', "Automatisation activée pour ce client")}
                  </label>
                </div>
                <div className="edn-form-group">
                  <label>{t('clientsList.disabledCarriers', 'Transp. désactivés')}</label>
                  <small className="edn-form-help">{t('clientEDNDetail.disabledCarriersHelp', "Les transporteurs désactivés ne sont pas utilisés par l'automatisation pour ce client.")}</small>
                  {carriers.length > 0 ? (
                    <div className="disabled-carriers-list">
                      {carriers.map((carrier) => {
                        const isDisabled = (editForm.disabled_carriers || []).includes(carrier.name);
                        return (
                          <label
                            key={carrier.id}
                            className={`carrier-checkbox-item ${isDisabled ? 'carrier-disabled' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isDisabled}
                              onChange={(e) => {
                                const current = editForm.disabled_carriers || [];
                                const updated = e.target.checked
                                  ? [...current, carrier.name]
                                  : current.filter((c) => c !== carrier.name);
                                setEditForm((f) => ({ ...f, disabled_carriers: updated }));
                              }}
                            />
                            <FaTruck className="carrier-icon" />
                            <span className="carrier-name">{carrier.name}</span>
                            {isDisabled && <FaBan className="carrier-ban-icon" />}
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="no-carriers-text">{t('clientEDNDetail.noCarriersAvailable', 'Aucun transporteur disponible')}</p>
                  )}
                </div>

                <h3 className="edn-form-section-title">{t('clientEDNDetail.sectionSouffrance', 'Notifications souffrance')}</h3>
                <div className="edn-form-group">
                  <label>{t('clientEDNDetail.defaultEmail', 'Email de notification par défaut')}</label>
                  <input
                    type="email"
                    value={editForm.default_email || ''}
                    onChange={(e) => setEditForm((f) => ({ ...f, default_email: e.target.value }))}
                    placeholder={t('clientsInstructions.emailPlaceholder', 'Adresse email')}
                    className="edn-input"
                  />
                </div>
                <button
                  type="button"
                  className="edn-advanced-toggle"
                  onClick={() => setShowAdvancedRouting((v) => !v)}
                  aria-expanded={showAdvancedRouting}
                >
                  {showAdvancedRouting ? <FaChevronDown /> : <FaChevronRight />}
                  {t('clientEDNDetail.advancedRouting', 'Avancé (routage B2B / B2C)')}
                </button>
                {showAdvancedRouting && (
                  <div className="edn-form-grid">
                    <div className="edn-form-group">
                      <label>{t('clientEDNDetail.b2bEmail', 'Email B2B')}</label>
                      <input
                        type="email"
                        value={editForm.b2b_email || ''}
                        onChange={(e) => setEditForm((f) => ({ ...f, b2b_email: e.target.value }))}
                        placeholder={t('clientEDNDetail.optional', 'optionnel')}
                        className="edn-input"
                      />
                    </div>
                    <div className="edn-form-group">
                      <label>{t('clientEDNDetail.b2cEmail', 'Email B2C')}</label>
                      <input
                        type="email"
                        value={editForm.b2c_email || ''}
                        onChange={(e) => setEditForm((f) => ({ ...f, b2c_email: e.target.value }))}
                        placeholder={t('clientEDNDetail.optional', 'optionnel')}
                        className="edn-input"
                      />
                    </div>
                  </div>
                )}
                <div className="edn-form-grid">
                  <div className="edn-form-group">
                    <label>{t('clientEDNDetail.souffranceLanguage', 'Langue des messages')}</label>
                    <select
                      value={editForm.souffrance_language || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, souffrance_language: e.target.value }))}
                      className="edn-input"
                    >
                      <option value="">{t('clientEDNDetail.souffranceLanguageDefault', 'Par défaut (français)')}</option>
                      {SOUFFRANCE_LANGUAGES.map((lang) => (
                        <option key={lang} value={lang}>
                          {t(`clientEDNDetail.languages.${lang}`, lang)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="edn-form-group">
                  <label>{t('clientEDNDetail.souffranceInstructions', 'Instructions souffrance')}</label>
                  <small className="edn-form-help">
                    {t('clientEDNDetail.souffranceHelp', 'Spécifiques à ce client : la langue remplace la langue générale, les instructions s\'ajoutent aux instructions générales (page Clients).')}
                  </small>
                  <textarea
                    value={editForm.souffrance_instructions || ''}
                    onChange={(e) => setEditForm((f) => ({ ...f, souffrance_instructions: e.target.value }))}
                    rows={4}
                    placeholder={t('clientEDNDetail.souffranceInstructionsPlaceholder', 'Ex. toujours mentionner notre numéro de support...')}
                    className="edn-textarea"
                  />
                </div>

                <div className="edn-form-actions">
                  <Button variant="primary" onClick={handleSave} disabled={saving}>
                    <FaSave /> {saving ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
                  </Button>
                  <Button variant="secondary" onClick={handleCancelEdit} disabled={saving}>
                    <FaTimes /> {t('common.cancel', 'Annuler')}
                  </Button>
                </div>
              </div>
            )}
          </section>

          {/* ACTIVITY card — composition bar + proportion lists */}
          <section className="edn-card edn-activity-card">
            <div className="edn-card-title">
              <h2>{t('clientEDNDetail.activity', 'Activité')}</h2>
            </div>
            {statsLoading ? (
              <div className="edn-skeleton" aria-busy="true">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="edn-skeleton-row" />
                ))}
              </div>
            ) : !activity || activity.total === 0 ? (
              <p className="edn-muted-text">{t('clientEDNDetail.noActivity', 'Aucun ticket pour ce client.')}</p>
            ) : (
              <>
                <div className="edn-activity-hero">
                  <span className="edn-activity-total">{activity.total.toLocaleString('fr-FR')}</span>
                  <span className="edn-activity-label">{t('clientEDNDetail.totalTickets', 'tickets au total')}</span>
                </div>

                <div className="edn-status-bar" role="img" aria-label={t('clientEDNDetail.byStatus', 'Par statut')}>
                  {activity.byStatus.map((s) => (
                    <span
                      key={s.status}
                      className={`edn-seg ${getStatusClass(s.status)}`}
                      style={{ flexGrow: s.count }}
                      title={`${getStatusLabel(t, s.status)} — ${s.count}`}
                    />
                  ))}
                </div>
                <div className="edn-status-legend">
                  {activity.shown.map((s) => (
                    <span key={s.status} className="edn-legend-item">
                      <span className={`edn-dot ${getStatusClass(s.status)}`} aria-hidden="true" />
                      {getStatusLabel(t, s.status)}
                      <strong>{s.count}</strong>
                    </span>
                  ))}
                  {activity.restCount > 0 && (
                    <span className="edn-legend-item">
                      <span className="edn-dot" aria-hidden="true" />
                      {t('clientEDNDetail.others', 'Autres')}
                      <strong>{activity.restCount}</strong>
                    </span>
                  )}
                </div>

                {activity.carriersTop.length > 0 && (
                  <div className="edn-activity-block">
                    <h4>{t('clientEDNDetail.topCarriers', 'Principaux transporteurs')}</h4>
                    {activity.carriersTop.map((c) => (
                      <div key={c.carrier_name} className="edn-meter-row">
                        <span className="edn-meter-name" title={c.carrier_name}>{c.carrier_name}</span>
                        <span className="edn-meter" aria-hidden="true">
                          <span style={{ width: `${Math.max(4, Math.round((c.count / activity.carriersMax) * 100))}%` }} />
                        </span>
                        <span className="edn-meter-count">{c.count}</span>
                      </div>
                    ))}
                  </div>
                )}

                {activity.topicsTop.length > 0 && (
                  <div className="edn-activity-block">
                    <h4>{t('clientEDNDetail.topicDistribution', 'Sujets')}</h4>
                    {activity.topicsTop.map((tp) => (
                      <div key={tp.topic} className="edn-meter-row">
                        <span className="edn-meter-name" title={tp.topic}>{tp.topic}</span>
                        <span className="edn-meter" aria-hidden="true">
                          <span style={{ width: `${Math.max(4, Math.round((tp.count / activity.topicsMax) * 100))}%` }} />
                        </span>
                        <span className="edn-meter-count">{tp.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}

      {/* TICKETS — dense table, URL-persisted page & filters */}
      {client && !clientError && (
        <section className="edn-card edn-tickets-card">
          <div className="edn-tickets-toolbar">
            <h2>{t('clientEDNDetail.tickets', 'Tickets')}</h2>
            <div className="edn-tickets-filters">
              <select
                value={statusFilter}
                onChange={(e) => updateTicketParams({ tstatus: e.target.value, tpage: null })}
                className="edn-filter-select"
              >
                <option value="">{t('allStatuses', 'Tous les statuts')}</option>
                {ALL_STATUS_MAPPING.map(({ value }) => (
                  <option key={value} value={value}>
                    {getStatusLabel(t, value)}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={carrierFilter}
                onChange={(e) => updateTicketParams({ tcarrier: e.target.value, tpage: null }, { replace: true })}
                placeholder={t('clientEDNDetail.filterCarrier', 'Filtrer par transporteur...')}
                className="edn-filter-input"
              />
            </div>
            <div className="edn-tickets-pager">
              <button
                type="button"
                onClick={() => updateTicketParams({ tpage: ticketsPage - 1 <= 1 ? null : ticketsPage - 1 })}
                disabled={ticketsPage === 1 || ticketsLoading}
                aria-label={t('common.previous')}
              >
                ‹
              </button>
              <span>{t('common.page', 'Page')} {ticketsPage}</span>
              <button
                type="button"
                onClick={() => updateTicketParams({ tpage: ticketsPage + 1 })}
                disabled={!ticketsHasMore || ticketsLoading}
                aria-label={t('common.next')}
              >
                ›
              </button>
            </div>
          </div>

          {ticketsLoading ? (
            <div className="edn-skeleton" aria-busy="true">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="edn-skeleton-row" />
              ))}
            </div>
          ) : ticketsError ? (
            <div className="edn-detail-state edn-detail-state-error">
              <FaExclamationTriangle />
              <p>{ticketsError}</p>
              <button type="button" className="u-btn u-btn--secondary" onClick={fetchTickets}>
                {t('common.retry', 'Réessayer')}
              </button>
            </div>
          ) : !tickets.length ? (
            <div className="edn-detail-state edn-detail-state-empty">
              <FaTicketAlt />
              <p>{t('clientEDNDetail.noTickets', 'Aucun ticket pour ce client')}</p>
            </div>
          ) : (
            <div className="edn-tickets-scroll">
              <table className="edn-tickets-table">
                <colgroup>
                  <col />
                  <col style={{ width: 160 }} />
                  <col style={{ width: 140 }} />
                  <col style={{ width: 200 }} />
                  <col style={{ width: 110 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t('subject', 'Sujet')}</th>
                    <th>{t('carrier', 'Transporteur')}</th>
                    <th>{t('topicColumn', 'Motif')}</th>
                    <th>{t('status', 'Statut')}</th>
                    <th>{t('updatedAt', 'Maj le')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      onClick={() => handleTicketClick(ticket.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleTicketClick(ticket.id);
                        }
                      }}
                    >
                      <td className="edn-t-subject" title={ticket.subject || ''}>
                        {ticket.redirected_to && (
                          <FaShareSquare
                            className="edn-redirect-icon"
                            title={`${t('clientEDNDetail.forwardedTo', 'Transféré à')} ${ticket.redirected_to}`}
                          />
                        )}
                        {ticket.subject || t('clientEDNDetail.noSubject', 'Sans sujet')}
                      </td>
                      <td className="edn-t-secondary" title={ticket.carrier_name || ''}>
                        {ticket.carrier_name || <span className="edn-muted-dash">–</span>}
                      </td>
                      <td className="edn-t-secondary" title={ticket.topic || ''}>
                        {ticket.topic || <span className="edn-muted-dash">–</span>}
                      </td>
                      <td>
                        <span className="edn-status-chip" title={getStatusLabel(t, ticket.status)}>
                          <span className={`edn-dot ${getStatusClass(ticket.status)}`} aria-hidden="true" />
                          <span className="edn-status-chip-label">{getStatusLabel(t, ticket.status)}</span>
                        </span>
                      </td>
                      <td className="edn-t-date" title={formatDateTime(ticket.updated_at)}>
                        {formatRelativeDate(ticket.updated_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ERP API ACCESS — collapsible, admin-only concern */}
      {client && !clientError && (
        <section className="edn-card edn-api-card">
          <button
            type="button"
            className="edn-api-header"
            onClick={() => setShowApiSection((v) => !v)}
            aria-expanded={showApiSection}
          >
            <div className="edn-api-header-title">
              <FaPlug aria-hidden="true" />
              <h2>{t('clientEDNDetail.apiAccess', 'Accès API ERP')}</h2>
            </div>
            <div className="edn-api-header-meta">
              <span className={`edn-api-badge ${apiAccess?.enabled ? 'enabled' : 'disabled'}`}>
                {apiAccess?.enabled
                  ? t('clientEDNDetail.apiActive', 'Actif')
                  : t('clientEDNDetail.apiInactive', 'Inactif')}
              </span>
              {showApiSection ? <FaChevronDown /> : <FaChevronRight />}
            </div>
          </button>

          {showApiSection && (
            <div className="edn-api-body">
              <p className="edn-api-description">
                {t(
                  'clientEDNDetail.apiAccessDescription',
                  "Activez une clé API privée pour que l'ERP de ce client crée, consulte et mette à jour ses tickets directement. Les tickets créés ainsi sont routés vers l'IA comme n'importe quel autre canal.",
                )}
              </p>

              {apiAccessError && (
                <div className="edn-alert edn-alert-error">{apiAccessError}</div>
              )}

              {apiAccessLoading ? (
                <div className="edn-skeleton" aria-busy="true">
                  <div className="edn-skeleton-row" />
                  <div className="edn-skeleton-row" />
                </div>
              ) : (
                <>
                  {apiAccess && (apiAccess.key_prefix || apiAccess.last_used_at || apiAccess.created_at) && (
                    <div className="edn-api-meta">
                      {apiAccess.key_prefix && (
                        <span><FaKey aria-hidden="true" /> {apiAccess.key_prefix}••••••••</span>
                      )}
                      {apiAccess.created_at && (
                        <span>{t('clientEDNDetail.apiCreatedAt', 'Créée le')} {formatDateTime(apiAccess.created_at)}</span>
                      )}
                      <span>{t('clientEDNDetail.apiLastUsed', 'Dernière utilisation')} : {formatDateTime(apiAccess.last_used_at)}</span>
                    </div>
                  )}

                  {generatedApiKey && (
                    <div className="edn-api-key-reveal">
                      <div className="edn-alert edn-api-key-warning">
                        {t('clientEDNDetail.apiKeyWarning', 'Copiez cette clé maintenant — elle n\'est affichée qu\'une seule fois.')}
                      </div>
                      <div className="edn-api-key-box">
                        <code>{generatedApiKey}</code>
                        <button type="button" className="u-btn u-btn--secondary" onClick={handleCopyApiKey}>
                          {apiKeyCopied ? <FaCheck /> : <FaCopy />}
                          {apiKeyCopied ? t('common.copied', 'Copié') : t('common.copy', 'Copier')}
                        </button>
                      </div>
                    </div>
                  )}

                  {apiAccess?.enabled && (
                    <div className="edn-api-endpoints">
                      <h4>{t('clientEDNDetail.apiEndpoints', 'Endpoints')}</h4>
                      <p className="edn-api-endpoints-help">
                        {t('clientEDNDetail.apiEndpointsHelp', "Envoyez la clé dans l'en-tête")} <code>X-API-Key</code>
                      </p>
                      <ul className="edn-api-endpoint-list">
                        <li><span className="edn-api-verb edn-api-verb-post">POST</span> <code>{apiBaseUrl}/api/integrations/tickets</code></li>
                        <li><span className="edn-api-verb edn-api-verb-get">GET</span> <code>{apiBaseUrl}/api/integrations/tickets</code></li>
                        <li><span className="edn-api-verb edn-api-verb-get">GET</span> <code>{apiBaseUrl}/api/integrations/tickets/&#123;ticket_id&#125;</code></li>
                        <li><span className="edn-api-verb edn-api-verb-patch">PATCH</span> <code>{apiBaseUrl}/api/integrations/tickets/&#123;ticket_id&#125;</code></li>
                      </ul>
                    </div>
                  )}

                  <div className="edn-api-actions">
                    <Button variant="primary" onClick={handleGenerateApiKey} disabled={apiAccessBusy}>
                      <FaKey />{' '}
                      {apiAccessBusy
                        ? t('common.saving', 'Enregistrement...')
                        : apiAccess?.enabled
                          ? t('clientEDNDetail.apiRotate', 'Régénérer la clé')
                          : t('clientEDNDetail.apiActivate', "Activer l'accès API")}
                    </Button>
                    {apiAccess?.enabled && (
                      <Button variant="secondary" onClick={handleRevokeApiKey} disabled={apiAccessBusy}>
                        <FaTrashAlt /> {t('clientEDNDetail.apiRevoke', 'Révoquer')}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default ClientEDNDetail;
